import { useEffect, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { API_BASE_URL } from "../config/api";

// Backend origin from the single source of truth (src/config/api.ts):
// dev/run → local, production/push → prod.
const API_URL = API_BASE_URL;

// Events TV → parent during co-watching. The data shape is event-type
// specific:
//   session_started   { sessionId, videoId, videoTitle }
//   heartbeat         { sessionId, secondsWatched, progress, paused? }
//   question_shown    { sessionId, question: { id, prompt, options, correct }, options }
//   answer_submitted  { sessionId, questionId, selectedOption, correct }
//   question_dismissed{ sessionId, questionId }
//   session_ended     { sessionId }
//   generation_ready  { videoId, videoTitle, hlsUrl }
export type TvEventType =
  | "session_started"
  | "heartbeat"
  | "question_shown"
  | "answer_submitted"
  | "question_dismissed"
  | "session_ended"
  | "generation_ready";

type TvEvent = {
  type: TvEventType;
  parentId: string;
  childId?: string;
  // Multi-TV: stamped server-side from the originating socket's handshake
  // (clients can't spoof it). Value is the PairingCode.id for tvId-aware
  // TVs, or the string 'legacy' for pre-rollout sockets.
  tvId?: string;
  data?: Record<string, unknown>;
};

// `play_video` carries `videoId` + `childId` so the TV knows what to play
// and which kid profile to log the session against. The other actions act
// on whatever's already playing and don't need additional context.
// `unpaired` is fired by the backend when the parent removes this TV from
// their account — the TV should drop its session and return to Pairing.
// `forced_unpair` is fired when the parent unpairs ONE specific device via
// the multi-TV endpoint; only the sockets bound to that device receive it.
export type ParentCommandAction =
  | { action: "pause" | "resume" | "stop" | "unpaired" | "forced_unpair" }
  | { action: "play_video"; videoId: string; childId: string };

type ParentCommand = {
  parentId?: string;
  action:
    | "pause"
    | "resume"
    | "stop"
    | "play_video"
    | "unpaired"
    | "forced_unpair";
  videoId?: string;
  childId?: string;
  tvId?: string;
};

// tv_status now arrives in two shapes:
//   - Aggregate (legacy parent pill): { parentId, online }
//   - Per-device: { parentId, tvId, online }
// Callers that only care about "any TV online" can read `online`; the
// per-device flavor is the source of truth for the multi-TV list.
type TvStatus = { parentId: string; online: boolean; tvId?: string };

type UseSessionSocketOptions = {
  parentId: string | null;
  role: "parent" | "tv";
  // Multi-TV handshake: when the role is "tv", the caller passes the
  // device's tvId (from /v1/pairing/exchange) so the gateway can bucket
  // this socket under (parentId, tvId) and target it from the parent.
  // Omitting it on a TV socket still works — the gateway buckets it as
  // 'legacy' and keeps the legacy single-TV behavior.
  tvId?: string | null;
  onTvEvent?: (event: TvEvent) => void;
  onParentCommand?: (command: ParentCommand) => void;
  onTvStatus?: (status: TvStatus) => void;
};

export function useSessionSocket({
  parentId,
  role,
  tvId,
  onTvEvent,
  onParentCommand,
  onTvStatus,
}: UseSessionSocketOptions) {
  const socketRef = useRef<Socket | null>(null);
  const callbacksRef = useRef({ onTvEvent, onParentCommand, onTvStatus });
  callbacksRef.current = { onTvEvent, onParentCommand, onTvStatus };

  useEffect(() => {
    if (!parentId) return;

    const socket = io(`${API_URL}/session`, {
      // Include tvId on TV handshakes so the backend gateway can route
      // parent_command / forced_unpair to a specific device. Spread is
      // guarded so a missing tvId doesn't add an empty string query param.
      query: {
        parentId,
        role,
        ...(role === "tv" && tvId ? { tvId } : {}),
      },
      transports: ["websocket"],
    });

    socket.on("tv_event", (event: TvEvent) => {
      callbacksRef.current.onTvEvent?.(event);
    });

    socket.on("parent_command", (command: ParentCommand) => {
      callbacksRef.current.onParentCommand?.(command);
    });

    socket.on("tv_status", (status: TvStatus) => {
      callbacksRef.current.onTvStatus?.(status);
    });

    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // tvId is in deps so the TV reconnects under its assigned device
    // bucket as soon as pairing-exchange hydrates it (it can arrive a
    // moment after parentId).
  }, [parentId, role, tvId]);

  const emitTvEvent = useCallback((event: TvEvent) => {
    socketRef.current?.emit("tv_event", event);
  }, []);

  // Overloaded: simple actions take just a string, while `play_video` needs
  // extra payload. We accept either the legacy string form or a richer
  // object so existing call sites keep working without churn.
  // Optional `tvId` (second positional or on the object) targets a single
  // device; omit it to fan-out to every TV for the parent (legacy semantics).
  const sendParentCommand = useCallback(
    (
      actionOrCommand:
        | "pause"
        | "resume"
        | "stop"
        | {
            action: "pause" | "resume" | "stop";
            tvId?: string;
          }
        | {
            action: "play_video";
            videoId: string;
            childId: string;
            tvId?: string;
          },
      tvId?: string,
    ) => {
      if (!parentId) return;
      if (typeof actionOrCommand === "string") {
        socketRef.current?.emit("parent_command", {
          parentId,
          action: actionOrCommand,
          ...(tvId ? { tvId } : {}),
        });
      } else {
        socketRef.current?.emit("parent_command", {
          parentId,
          ...actionOrCommand,
        });
      }
    },
    [parentId],
  );

  return { emitTvEvent, sendParentCommand };
}
